import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { debounceTime, skipWhile, startWith, switchMap, map, take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UserProfile, GraphApi, ExternalUserState } from '@content-platform/web-angular-administration-shared-data-access-graph-api';
import { Store, select } from '@ngrx/store';
import { Auth, authSelectors } from '@content-platform/web-angular-shared-util-auth';
import { AuthDebugFlagNames } from '@content-platform/web-angular-shared-util-auth';

import { DebugFlagSettings, DebugFlag, DebugFlagsService } from '@content-platform/web-angular-shared-util-development';
import { LoggerService, actionEnum, eventTypeEnum } from '@content-platform/web-angular-shared-util-logging';
import { trim } from 'lodash';

const IMPERSONATE_USER_FLAG = 'impersonate_user';

export interface DebugFlagsDialogData {
  flags: DebugFlag[];
  settings: DebugFlagSettings;
}

@Component({
  selector: 'app-debug-flags-dialog',
  templateUrl: './debug-flags-dialog.component.html',
  styleUrls: ['./debug-flags-dialog.component.scss']
})
export class DebugFlagsDialogComponent implements OnInit {
  debugFlags: DebugFlag[] = [];
  flagSettingsList: DebugFlagSettings[] = [];
  flagMetadata: DebugFlagSettings = {};
  flagValues = {};
  filteredUsers$: Observable<UserProfile[]>;
  userSearchCtrl = new FormControl();
  loginUser$: Observable<any>;
  loggedInUserEmail: string;
  loggedInUserName: string;
  impersonated: boolean;

  constructor(
    private dialogRef: MatDialogRef<DebugFlagsDialogComponent>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public data: DebugFlagsDialogData,
    private graphApi: GraphApi,
    private logger: LoggerService,
    store: Store<Auth>,
    @Optional() private debugFlagService: DebugFlagsService
  ) {
    this.loginUser$ = store.pipe(select(authSelectors.getADUser));
    this.data = this.data || { flags: [], settings: {} };
    this.debugFlags = this.data.flags || [];
    // Convert settings object to array for use in selection list
    this.flagSettingsList = Object.keys(this.data.settings || {}).map(key => {
      this.flagMetadata[key] = this.data.settings[key];
      this.flagValues[key] = this.makeSelectionValue(key, this.flagMetadata[key] || false);
      return this.flagValues[key];
    });
  }
  validateEmail(emailControl: FormControl) {
    const user = emailControl.value;
    if (emailControl.value && this.loggedInUserEmail && this.loggedInUserName) {
      if (typeof user === 'string') {
        if (
          user.trim().toLowerCase() === this.loggedInUserEmail.toLowerCase() ||
          user.trim() === this.loggedInUserName.toLowerCase()
        ) {
          return {
            loggedUserFound: 'logged in user found'
          };
        }
      } else if (typeof user === 'object') {
        const userName = `${user.surname} ${user.givenName}`;
        if (
          user.mail.trim().toLowerCase() === this.loggedInUserEmail.toLowerCase() ||
          userName.trim().toLowerCase() === this.loggedInUserName.toLowerCase()
        ) {
          return {
            loggedUserFound: 'logged in user found'
          };
        }
      } else {
        return {
          userNotFound: 'user not found'
        };
      }
    }
    return null;
  }
  ngOnInit() {
    this.userSearchCtrl = new FormControl('', [this.validateEmail.bind(this)]);
    this.loginUser$.subscribe(({ profile }) => {
      if (this.debugFlagService) {
        if (this.debugFlagService.hasDebugFlag(AuthDebugFlagNames.Impersonate)) {
          this.loggedInUserName = profile.name;
          this.loggedInUserEmail = profile.mail;
          this.impersonated = true;
        } else {
          this.loggedInUserName = profile.name;
          this.loggedInUserEmail = profile.unique_name;
        }
      }
    });
    this.debugFlags
      .filter(flag => flag.inputType === 'auto-complete')
      .forEach(flag => this.userSearchCtrl.setValue(this.flagMetadata[flag.name]));
    this.filteredUsers$ = <Observable<UserProfile[]>>this.userSearchCtrl.valueChanges.pipe(
      startWith(''),
      skipWhile(val => trim(val) === ''),
      debounceTime(500),
      switchMap(user => {
        return this.graphApi.searchUsers(trim(user) as string);
      }),
      map(res =>
        res.filter(output => {
          return (
            output.mail !== this.loggedInUserEmail &&
            output.userState !== ExternalUserState.PENDING_ACCEPTANCE
          );
        })
      )
    );
  }

  private makeSelectionValue(flagName: string, value): DebugFlagSettings {
    const selectionValue = this.flagValues[flagName] || {};
    selectionValue[flagName] = value;
    return selectionValue;
  }

  getSelectionValue(flag: DebugFlag) {
    // set valid user value to flagvalues
    if (
      flag.name === IMPERSONATE_USER_FLAG &&
      this.userSearchCtrl.value &&
      typeof this.userSearchCtrl.value === 'object' &&
      !this.userSearchCtrl.hasError('loggedUserFound')
    ) {
      if (!!this.userSearchCtrl.value) {
        this.flagValues[flag.name] = this.makeSelectionValue(flag.name, '');
        this.userSearchCtrl.setValue('');
      } else {
        this.flagValues[flag.name] = this.makeSelectionValue(flag.name, this.userSearchCtrl.value);
      }
    } else if (
      flag.name === IMPERSONATE_USER_FLAG &&
      this.userSearchCtrl.value &&
      typeof this.userSearchCtrl.value !== 'object' &&
      !this.userSearchCtrl.hasError('loggedUserFound')
    ) {
      this.flagValues[flag.name] = this.makeSelectionValue(flag.name, '');
      this.userSearchCtrl.setValue('');
    } else if (flag.name !== IMPERSONATE_USER_FLAG) {
      this.flagValues[flag.name] = this.makeSelectionValue(flag.name, true);
    }
    // check for value whether it is selected earlier and remove duplicate
    if (this.flagSettingsList.includes(this.flagValues[flag.name])) {
      const index: number = this.flagSettingsList.indexOf(this.flagValues[flag.name]);
      if (index !== -1) {
        this.flagSettingsList.splice(index, 1);
      }
    } else {
      // Check for flagvalues and avoid pushing empty / undefined value if any
      if (!!this.flagValues[flag.name]) {
        this.flagSettingsList.push(this.flagValues[flag.name]);
      }
    }
  }

  onFieldClick($event: MouseEvent) {
    // Make it so it won't check the box
    $event.stopPropagation();
  }

  onUpdateFlagMetaData(flag: DebugFlag, formValue: string) {
    const flagSetting = this.flagValues[flag.name];
    if (flagSetting) {
      flagSetting[flag.name] = formValue;
    }
  }

  updateReduceFlagSettings(data: DebugFlagSettings[]): DebugFlagSettings {
    // Convert list back to settings object
    return data.reduce((collector, current) => {
      Object.keys(current).forEach(key => (collector[key] = current[key]));
      return collector;
    }, {});
  }

  logtoMegabeacon(data: DebugFlagSettings[]) {
    this.logger
      .doLogMegaBeacon(eventTypeEnum.DEBUG_FLAG, actionEnum.CREATE, '', data)
      .pipe(take(1))
      .subscribe(res => res);
  }
  setCheckboxValue(flag: DebugFlag) {
    return this.flagSettingsList.find(obj => obj[flag.name]);
  }
  cancel() {
    this.dialogRef.close();
  }

  selectUser(event: MatAutocompleteSelectedEvent): void {
    this.userSearchCtrl.setValue(event.option.value);
    this.flagValues[IMPERSONATE_USER_FLAG] = this.makeSelectionValue(
      IMPERSONATE_USER_FLAG,
      this.userSearchCtrl.value
    );
    if (!!this.flagValues[IMPERSONATE_USER_FLAG]) {
      this.flagSettingsList.push(this.flagValues[IMPERSONATE_USER_FLAG]);
    }
  }

  displayFn(user?: UserProfile): string | undefined {
    if (!user) {
      return '';
    } else {
      return `${user.displayName} (${user.mail})`;
    }
  }
}
